import { useMemo } from 'react';
import { useGT } from 'gt-react';
import {
  SingleSelect,
  type SingleSelectOption,
} from '@/components/common/SingleSelect';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import type { VersionedEntityAdapter, VersionedEntityRef } from './types';

// Sentinel option id for the "latest published" choice in the version select.
const LATEST_PUBLISHED_ID = '__latest_published__';

/**
 * Composable selector for a versioned entity (workflows, email templates,
 * template blocks, …). Pairs a lineage picker with a version picker that offers
 * "Latest published" plus each finalized version. Picking an entity defaults to
 * "latest published" (`versionId: null`); the user can opt into pinning a
 * specific finalized version.
 *
 * Entity-specific data + copy come from the `adapter`; this component is
 * otherwise entity-agnostic. See {@link VersionedEntityAdapter}.
 */
export function VersionedEntitySelect({
  value,
  onChange,
  adapter,
  disabled,
  allowClear,
}: {
  value: VersionedEntityRef | null;
  onChange: (value: VersionedEntityRef | null) => void;
  adapter: VersionedEntityAdapter;
  disabled?: boolean;
  allowClear?: boolean;
}) {
  const gt = useGT();
  const noun =
    adapter.copy?.entityNoun ??
    gt('entity', { context: 'generic versioned entity noun' });

  const { useOptions, useVersions } = adapter;
  const {
    options,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useOptions();
  // Versions of the selected entity. Only meaningful once one is chosen.
  const {
    versions,
    isLoading: versionsLoading,
    isError: versionsError,
  } = useVersions(value?.entityId);

  function handleEntityChange(entityId: string) {
    if (!entityId) {
      onChange(null);
      return;
    }
    // Default to "latest published" (null) — the reference tracks whatever
    // version is currently pinned. The user can pin a specific finalized
    // version via the version selector below.
    onChange({ entityId, versionId: null });
  }

  // Version dropdown options: "Latest published" first, then finalized versions
  // newest-first. shouldAutoSort is disabled to preserve this ordering.
  const versionOptions: SingleSelectOption[] = useMemo(() => {
    const latest: SingleSelectOption = {
      id: LATEST_PUBLISHED_ID,
      label: gt('Latest published', {
        context: 'versioned entity selector option',
      }),
    };
    const finalized = versions
      .filter((v) => v.status === 'finalized')
      .sort((a, b) => b.version - a.version)
      .map((v) => ({
        id: v.id,
        label: v.isPinned
          ? gt('v{version} · published', {
              context: 'versioned entity selector pinned version label',
              version: v.version,
            })
          : gt('v{version}', {
              context: 'versioned entity selector version label',
              version: v.version,
            }),
      }));
    return [latest, ...finalized];
  }, [versions, gt]);

  // Resolve the selected version option. A specific id that isn't in the list
  // (legacy config, or the version was deleted) still renders with a fallback
  // label so the selection isn't silently lost.
  const selectedVersionOption: SingleSelectOption | null = useMemo(() => {
    if (!value) return null;
    if (value.versionId === null) {
      return versionOptions[0];
    }
    const match = versionOptions.find(
      (option) => option.id === value.versionId,
    );
    return (
      match ?? {
        id: value.versionId,
        label: gt('Selected version', {
          context: 'versioned entity selector fallback label',
        }),
      }
    );
  }, [value, versionOptions, gt]);

  function handleVersionChange(option: SingleSelectOption | null) {
    if (!value) return;
    onChange({
      entityId: value.entityId,
      versionId:
        !option || option.id === LATEST_PUBLISHED_ID ? null : option.id,
    });
  }

  return (
    <div className="space-y-2">
      <Autocomplete
        items={options}
        value={value?.entityId || null}
        getValue={(option) => option.id}
        getLabel={(option) => option.name}
        onValueChange={(next) => handleEntityChange(next ?? '')}
        deselectable
        labels={pickerLabels({
          placeholder: optionsLoading
            ? gt('Loading {noun}...', {
                context: 'versioned entity selector loading placeholder',
                noun,
              })
            : gt('Choose a {noun}', {
                context: 'versioned entity selector placeholder',
                noun,
              }),
          search: gt('Search {noun}...', {
            context: 'versioned entity selector search placeholder',
            noun,
          }),
        })}
        disabled={disabled || optionsLoading || optionsError}
        allowClear={allowClear}
      />

      {value && (
        <SingleSelect
          options={versionOptions}
          value={selectedVersionOption}
          onChange={handleVersionChange}
          shouldAutoSort={false}
          placeholder={
            versionsLoading
              ? gt('Loading versions...', {
                  context:
                    'versioned entity selector version loading placeholder',
                })
              : gt('Choose a version', {
                  context: 'versioned entity selector version placeholder',
                })
          }
          searchPlaceholder={gt('Search versions...', {
            context: 'versioned entity selector version search placeholder',
          })}
          disabled={disabled || versionsLoading || versionsError}
          size="sm"
          fullWidth
        />
      )}
    </div>
  );
}
